import { List, isImmutable } from 'immutable'

const EMPTY_TEXT_TOKENS = [{ value: '' }]

export const getTextValue = (state, dataPath, isRepeat, index, type) => {
  const defaultValue =
    {
      old: '',
      new: '',
      draft: '',
    }[type] || ''

  if (isRepeat) {
    const rows = state.getIn([...dataPath, type], List())

    if (isImmutable(rows)) {
      return rows.get(Number(index), rows.get(String(index), defaultValue))
    }

    if (['string', 'number', 'boolean'].includes(typeof rows)) {
      return String(rows)
    }

    return defaultValue
  }

  return state.getIn([...dataPath, type], defaultValue)
}

export const getTextTokenCursorValue = ({ isPreview, textHasDiff, showTooltip }) => {
  if (!isPreview) {
    return 'default'
  }

  if (textHasDiff) {
    return showTooltip ? 'pointer' : 'default'
  }

  return 'text'
}

export const getTextTokensIndicesForAcceptAction = (selectedTextTokenIndex, textTokens) => {
  let textTokenIndexToRemove, textTokenIndexToRemain

  const selectedTextToken = textTokens.at(selectedTextTokenIndex)

  if (selectedTextToken.added) {
    textTokenIndexToRemain = selectedTextTokenIndex

    const previousTextTokenIndex = selectedTextTokenIndex - 1
    const previousTextToken = textTokens.at(previousTextTokenIndex)

    if (previousTextToken && previousTextToken.removed) {
      textTokenIndexToRemove = previousTextTokenIndex
    }
  }

  if (selectedTextToken.removed) {
    textTokenIndexToRemove = selectedTextTokenIndex

    const nextTextTokenIndex = selectedTextTokenIndex + 1
    const nextTextToken = textTokens.at(nextTextTokenIndex)

    if (nextTextToken && nextTextToken.added) {
      textTokenIndexToRemain = nextTextTokenIndex
    }
  }

  return {
    textTokenIndexToRemove,
    textTokenIndexToRemain,
  }
}

export const getTextTokensIndicesForRevertAction = (selectedTextTokenIndex, textTokens) => {
  let textTokenIndexToRemove, textTokenIndexToRemain

  const selectedTextToken = textTokens.at(selectedTextTokenIndex)

  if (selectedTextToken.added) {
    textTokenIndexToRemove = selectedTextTokenIndex

    const previousTextTokenIndex = selectedTextTokenIndex - 1
    const previousTextToken = textTokens.at(previousTextTokenIndex)

    if (previousTextToken && previousTextToken.removed) {
      textTokenIndexToRemain = previousTextTokenIndex
    }
  }

  if (selectedTextToken.removed) {
    textTokenIndexToRemain = selectedTextTokenIndex

    const nextTextTokenIndex = selectedTextTokenIndex + 1
    const nextTextToken = textTokens.at(nextTextTokenIndex)

    if (nextTextToken && nextTextToken.added) {
      textTokenIndexToRemove = nextTextTokenIndex
    }
  }

  return {
    textTokenIndexToRemove,
    textTokenIndexToRemain,
  }
}

export const handleAcceptChange = (textTokens, textTokensIndices) => {
  const updatedTextTokens = textTokens
    .map((token, index) => {
      if (index === textTokensIndices.textTokenIndexToRemain) {
        return {
          ...token,
          added: false,
        }
      }

      return token
    })
    .filter((_, index) => {
      if (index === textTokensIndices.textTokenIndexToRemove) {
        return false
      }

      return true
    })

  return updatedTextTokens.length ? updatedTextTokens : EMPTY_TEXT_TOKENS
}

export const handleRevertChange = (textTokens, textTokensIndices) => {
  const updatedTextTokens = textTokens
    .map((token, index) => {
      if (index === textTokensIndices.textTokenIndexToRemain) {
        return {
          ...token,
          removed: false,
        }
      }

      return token
    })
    .filter((_, index) => {
      if (index === textTokensIndices.textTokenIndexToRemove) {
        return false
      }

      return true
    })

  return updatedTextTokens.length ? updatedTextTokens : EMPTY_TEXT_TOKENS
}

export const handleAcceptAllChanges = (textTokens) => {
  const updatedTextTokens = textTokens
    .filter((token) => !token.removed)
    .map((token) => ({
      ...token,
      added: false,
    }))

  return updatedTextTokens.length ? updatedTextTokens : EMPTY_TEXT_TOKENS
}

export const handleRevertAllChanges = (textTokens) => {
  const updatedTextTokens = textTokens
    .filter((token) => !token.added)
    .map((token) => ({
      ...token,
      removed: false,
    }))

  return updatedTextTokens.length ? updatedTextTokens : EMPTY_TEXT_TOKENS
}

export const getRemainingChangesCount = (textTokens) => {
  let changesCount = 0

  textTokens.forEach((token, index) => {
    const prevToken = textTokens[index - 1]
    const nextToken = textTokens[index + 1]

    if (token.added && prevToken?.removed) {
      return
    }

    if (token.removed && nextToken?.added) {
      changesCount++
      return
    }

    if (token.added || token.removed) {
      changesCount++
    }
  })

  return changesCount
}

export const getTextContentFromTokens = (textTokens) => {
  let textContent = ''

  textTokens.forEach((token) => {
    textContent += token.value
  })

  return textContent
}

export const minifyTextTokens = (textTokens) => {
  return textTokens.map((textToken) => {
    const minifiedTextToken = {
      v: textToken.value,
    }

    if ('added' in textToken) {
      minifiedTextToken.a = textToken.added ? 1 : 0
    }

    if ('removed' in textToken) {
      minifiedTextToken.r = textToken.removed ? 1 : 0
    }

    return minifiedTextToken
  })
}

export const unminifyTextTokens = (textTokens) => {
  return textTokens.map((minifiedTextToken) => {
    const textToken = {
      value: minifiedTextToken.v,
    }

    if ('a' in minifiedTextToken) {
      textToken.added = minifiedTextToken.a === 1
    }

    if ('r' in minifiedTextToken) {
      textToken.removed = minifiedTextToken.r === 1
    }

    return textToken
  })
}

export const getSingleElementStateByIndex = (repeatElementState, index) => {
  return Object.keys(repeatElementState).reduce((accumulator, key) => {
    accumulator[key] = repeatElementState[key][index]

    return accumulator
  }, {})
}
